<template>
  <div class="table-responsive">
    <table class="table">
      <thead>
        <tr>
          <th>System ID</th>
          <th>Name</th>
          <th>Dates</th>
          <th>Rep & Employee</th>
          <th v-if="renewalStatus">Current Plan(S) & Premium</th>
          <th v-if="renewalStatus">Approved Renewal Plan(s)</th>
        </tr>
      </thead>
      <tbody>
        <!-- First load: show quick buttons inside the table -->
        <tr v-if="isInitialLoad">
          <td colspan="6" class="empty-state-cell">
            <div class="quick-date-btns-inline d-flex align-items-center justify-content-center gap-2 flex-wrap mb-3">
              <button
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'all' }"
                @click="$emit('quick-filter', 'all')"
                title="All members"
              >
                <i class="fas fa-list me-1"></i>
                All Members
              </button>
              <button
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'today' }"
                @click="$emit('quick-filter', 'today')"
                title="Members enrolled today"
              >
                <i class="fas fa-calendar-day me-1"></i>
                Members Today
              </button>
              <button
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'week' }"
                @click="$emit('quick-filter', 'week')"
                title="Members enrolled this week"
              >
                <i class="fas fa-calendar-week me-1"></i>
                Members This Week
              </button>
              <button
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'month' }"
                @click="$emit('quick-filter', 'month')"
                title="Members enrolled this month"
              >
                <i class="fas fa-calendar-alt me-1"></i>
                Members This Month
              </button>
            </div>
            <div class="empty-state-msg">
              <i class="fas fa-search me-2"></i>
              Enter search criteria or use a quick search above to display results.
            </div>
          </td>
        </tr>

        <!-- After search: no results -->
        <tr v-else-if="!memberLength">
          <td colspan="6" align="center">No records found.</td>
        </tr>

        <!-- Data rows -->
        <tr
          v-else
          v-for="item in localMemberLists.records"
          :key="item.policy_id"
          :class="calssByStatus(item.status)"
          :id="item.status"
        >
          <td>
            <strong>{{ item.policy_id }}</strong
            ><br />
            <span
              :class="
                'id-status-' + renewalDisplayStatus(item).toLowerCase()
              "
              :style="(item.l713_status || item.ihaStatus) ? { color: '' } : {}"
            >
              <valid-icon v-if="String(item.l713_status || item.ihaStatus || '').toLowerCase() == 'active'" :is-valid="true" class="valid-icon" />
              {{ item.l713_status || item.ihaStatus || item.status }}
              <template v-if="showHealthQuestionnaires && hasHealthQuestionnaires(item)">{{ item.ihaSubCategory }}</template>
            </span>
            <br />
            <!-- <template v-if="item.billing_date">
              <strong>Recurring Date: </strong>
              {{ item.billing_date }}
            </template> -->
            <!-- <template v-if="item.billing_date">
              <strong>Recurring Date: </strong>
              <div class="d-flex align-items-center recurring-datewrapper">
                <span> {{ item.billing_date }}</span>
                <custom-button v-if="isEnrollPrime"
                  title="Edit"
                  haveText="true"
                  iconClass="fas fa-pen edit-icon"
                  @onClicked="openModal(item, 'editRecurringDateModal')"
                  className="edit-modal-btn"
                />
              </div>
            </template> -->
          </td>
          <td>
            <strong>{{ item.first_name }} {{ item.last_name }}</strong
            ><br />
            <template v-if="item.employer_type === 'employer'">
              <strong class="text-red">EMPLOYEE</strong><br />
            </template>
            <span v-if="item.phone">
              {{ item.phone | us_phone }}
              <valid-icon v-if="item.info_validated" :is-valid="item.is_phone_valid" class="valid-icon" />
            </span>
            <br />
            <span v-if="item.user_email">
              {{ item.user_email }}
              <valid-icon v-if="item.info_validated" :is-valid="item.is_email_valid" class="valid-icon" />
            </span>

           <!-- <p>{{ item.user_address }}</p> -->
            
          </td>

          <td>

            <strong>Effective Date:</strong><br />{{ item?.effective_date
            }}<br />
            <template v-if="item?.renewal_date">
              <strong>Renewal Date:</strong><br />{{ item.renewal_date | date_format("YYYY-MM-DD", "MM/DD/YYYY", item.renewal_date) }}<br />
            </template>
            <template v-if="item.plan_history && item.plan_history?.length > 0">
              <div
                v-if="
                  item.status === 'TERMED' &&
                    item.plan_history[0].plan_status === 'TERMED'
                "
                style="color: red;"
              >
                <strong>Termed Date:</strong><br />{{
                  item.plan_history[0].plan_termed_date
                }}<br />
              </div>
              <div
                v-if="
                  item.status === 'WITHDRAWN' &&
                    item.plan_history[0].plan_status === 'WITHDRAWN'
                "
                style="color: red;"
              >
                <strong>Withdrawn Date:</strong><br />{{
                  item.plan_history[0].plan_termed_date
                }}<br />
              </div>
            </template>

            
          </td>
          <td>
            <strong>
              <router-link :to="{
                name: 'MemberListByRep',
                params: { repId: encodedId(item.agent_id) },
              }" target="_blank" class="route-link">
                {{ item.rep_first_name }} {{ item.rep_last_name }}
              </router-link>
            </strong>
            <br />
            {{ item.agent_code }}
            <br v-if="item.business_name && item.business_name !== 'N/A'" />
            <template v-if="item.business_name && item.business_name !== 'N/A'">
              Business Name: {{ item.business_name }}
            </template>
            <br />
            <template v-if="item.employer_name && !isEnrollPrime">
              Employer Name:
              <router-link :to="{
                name: 'MemberListByGroup',
                params: { groupId: encodedId(item.employer_id) },
              }" target="_blank" class="route-link">
                {{ item.employer_name }}
              </router-link>
            </template>
          </td>
          <td v-if="renewalStatus">
            <table v-if="renewalCurrentPlans(item).length" class="table premiums-table">
              <tr v-for="(planItem, index) in renewalCurrentPlans(item)" :key="planItem.plan_id || index">
                <td :class="isTermedPlan(planItem) ? 'text-red' : ''">
                  {{ planItem.plan_name_system }}
                  <template v-if="isTermedPlan(planItem) && (planItem.plan_termed_date || planItem.plan_term_date)">
                    <br />{{ `(${planItem.plan_termed_date || planItem.plan_term_date})` }}
                  </template>
                  <div class="d-flex align-items-center justify-content-start p-0">
                    <strong class="me-1">Tier:</strong>
                    <span>{{ planItem.plan_tier }}</span>
                  </div>
                </td>
                <td>{{ "$" + planItem.plan_price }}</td>
              </tr>
            </table>
            <span v-else class="text-muted">N/A</span>
          </td>

          <td v-if="renewalStatus">
            <span v-if="renewalDisplayStatus(item) === 'PENDING-RENEWAL-UNDERWRITING'" class="text-muted">N/A</span>
            <div v-else-if="renewalDisplayStatus(item) === 'ACTIVE'" class="renewal-approved-tick">
              <i class="fas fa-check-circle"></i>
            </div>
            <template v-else>
            <table v-if="item.renewal_plan_details?.length" class="table premiums-table">
              <tr v-for="plan in item.renewal_plan_details" :key="plan.plan_id">
                <td>
                  {{ plan.plan_name }}
                  <div class="d-flex align-items-center justify-content-start p-0">
                    <strong class="me-1">Tier:</strong>
                    <span>{{ plan.plan_tier }}</span>
                  </div>
                </td>
                <td>
                  <template v-if="hasRenewalPrice(plan)">
                    <strong v-if="plan.renewal_price !== plan.current_price">{{ "$" + plan.renewal_price }}</strong>
                    <span v-else>{{ "$" + plan.renewal_price }}</span>
                    <template v-if="plan.renewal_price !== plan.current_price">
                      <!--<br />
                      <small class="text-muted">(was ${{ plan.current_price }})</small> -->
                    </template>
                  </template>
                  <span v-else class="text-muted">N/A</span>
                </td>
              </tr>
            </table>
            <span v-else class="text-muted">N/A</span>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Include your modals here, unmodified -->
         <WaiveEfeeLogs
      :show="showWaiveEfeeLogs"
      :row="row"
      :type="'member'"
      @onCloseModal="closeWaiveEfeeLogs"
    />
    <member-doc-modal
      :member.sync="selectedMember"
      @onCloseModal="selectedMember = null"
    />
    <member-upload-media-modal :member.sync="row" @onCloseModal="row = null" />
    <member-email-modal :member.sync="row" @onCloseModal="row = null" />
    <member-resend-email-modal :member.sync="row" @onCloseModal="row = null" />
    <member-signature-email-modal :member.sync="row" @onCloseModal="row = null" />
    <member-payment-history-modal :member.sync="selectedMember" @onCloseModal="selectedMember = null"
      :showPaymentModal.sync="showPaymentModal" />
    <member-change-platform-modal :member.sync="row" @onCloseModal="closeChangePlatformModal"
      :showChangePlatformModal.sync="showChangePlatformModal"
    />
    <member-send-login-email-modal :member.sync="row" @onCloseModal="row = null" />
    <member-change-major-medical-modal :member.sync="row" @onCloseModal="row = null" />
    <approve-plan-modal :member.sync="row" @onCloseModal="row = null" />
    <effective-policy-date-modal
      :member.sync="row"
      @onCloseModal="row = null"
    />
    <default-otp-modal :row.sync="row" rowType="M" @onCloseModal="row = null" />
    <member-plan-termination-modal
      :member.sync="row"
      @onCloseModal="row = null"
    />
    <send-referral-link-to-user :member.sync="row" />
    <MemberPlanHistoryModal
      :member.sync="row"
      @onCloseModal="row = null"
      :showPlanHistoryModal.sync="showPlanHistoryModal"
    />
    <PlanRenewalSwitchModal :member.sync="row" />
    <member-notes-modal
      :member.sync="row"
      @onCloseModal="closeNotesModal"
      :showNotesModal.sync="showNotesModal"
    />
    <member-card-payments-modal
      :member.sync="row"
      @onCloseModal="closeCardPaymentsModal"
      :showCardPaymentsModal.sync="showCardPaymentsModal"
    />
    <EnrollCardRequestModal
      :row="selectedMember"
      @onCloseModal="selectedMember = null"
    />
    <term-policy-modal :member.sync="row" @onCloseModal="row = null" />
    <EditRecurringDateModal
      :member.sync="row"
      @onCloseModal="row = null"
      @onRecurringCloseModal="handleRecurringCloseModal"
    />
    <EditMemberTierModal
      :isOpen="isEditTierDialogOpen"
      :existingSpouse="true"
      @saveTier="handleTierUpdate"
      @onCloseModal="isEditTierDialogOpen = false"
      :members="editTierSelectedData"
      :policyId="Number(editTierSelectedData?.policy_id)"
    />
    <ScreenshotViewerModal
      :show="showScreenshotViewerModal"
      :screenshots-data="screenshotsData"
      @onCloseModal="showScreenshotViewerModal = false"
    />
       <WaiveEfeeModal
      :show="showWaiveFeeModal"
      @onCloseModal="closeWaiveEfeeModal"
      :row="row"
      :type="'member'"
      @onSuccess="closeWaiveEfeeModal"
    />
    <LogDisplayModal
      :isOpen="isLogModalOpen"
      @update:isOpen="isLogModalOpen = $event"
      :logData="switchLogData"
      :userId="selectedMemberId"
      :logType="'member'"
    />
    <HealthQuestionnaires
      v-model="showHealthQuestionnairesModal"
      :member="row"
      @close="closeHealthQuestionnairesModal"
    />
    <AddMemberNotesModal
      :row="row"
      :isOpen="showAddNotesModal"
      @close="showAddNotesModal = false"
    />
    <ViewMemberNotesModal 
      :isOpen="showViewNotesModal"
      :row="row"
      @close="showViewNotesModal = false"
    />
      <MedicationDetails 
      :show="showMedicationModal"
      :row="row"
      @onCloseModal="closeMedicationModal"
    />

    <RaiseIhaIssueModal
      :isOpen="showRaiseIhaIssueModal"
      :row="row"
      @close="showRaiseIhaIssueModal = false"
    />
    <MemberFileUploadAccess />
  </div>
</template>

<script>
import axios from "axios";
import MemberDocModal from "../modal/MemberDocModal.vue";
import MemberUploadMediaModal from "../modal/MemberUploadMediaModal.vue";
import MemberEmailModal from "../modal/MemberEmailModal.vue";
import MemberResendEmailModal from "../modal/MemberResendEmailModal.vue";
import MemberSignatureEmailModal from "../modal/MemberSignatureEmailModal.vue";
import MemberPaymentHistoryModal from "../modal/MemberPaymentHistoryModal.vue";
import MemberChangePlatformModal from "../modal/MemberChangePlatformModal.vue";
import MemberSendLoginEmailModal from "../modal/MemberSendLoginEmailModal.vue";
import MemberChangeMajorMedicalModal from "../modal/MemberChangeMajorMedicalModal.vue";
import ApprovePlanModal from "../modal/ApprovePlanModal.vue";
import EffectivePolicyDateModal from "../modal/EffectivePolicyDateModal.vue";
import DefaultOtpModal from "@/components/ui/DefaultOtpModal.vue";
import MemberPlanTerminationModal from "../modal/MemberPlanTerminationModal.vue";
import SendReferralLinkToUser from "../modal/SendReferralLinkToUser";
import MemberPlanHistoryModal from "../modal/MemberPlanHistoryModal";
import PlanRenewalSwitchModal from "../modal/PlanRenewalSwitchModal";
import MemberNotesModal from "../modal/MemberNotesModal.vue";
import { mapGetters } from "vuex";
import { restrictedReps } from "@/utils/constant";
import { checkRepAccess } from "@/utils/checkRepAccess";
import TermPolicyModal from '../modal/TermPolicyModal.vue'
import isEnrollPrimeUtil from '@/utils/isEnrollPrimeUtil';
import EditRecurringDateModal from "../modal/EditRecurringDate.vue";
import EditMemberTierModal from "../modal/EditMemberTier.vue";
import MemberCardPaymentsModal from "../modal/MemberCardPaymentsModal.vue";
import EnrollCardRequestModal from "../modal/EnrollCardRequestModal.vue";
import Api from "@/resource/Api";
import ScreenshotViewerModal from "../modal/ScreenshoViewer/ScreenshotViewerModal.vue";
import Helper from '@/resource/Helper';
import WaiveEfeeModal from "../modal/WaiveEfeeModal.vue";
import WaiveEfeeLogs from "../modal/WaiveEfeeLogsMember.vue";
import LogDisplayModal from "../modal/LogDisplayModal.vue";
import AddMemberNotesModal from "../modal/Notes/AddMemberNotesModal.vue";
import ViewMemberNotesModal from "../modal/Notes/ViewMemberNotesModal.vue";
import HealthQuestionnaires from "../modal/HealthQuestionnaires.vue";
import MedicationDetails from "../modal/MedicationDetails.vue";
import RaiseIhaIssueModal from "../modal/RaiseIhaIssueModal.vue";
import MemberFileUploadAccess from "../modal/MemberFileUploadAccess.vue";


export default {
  name: "RenewalClientsData",
  components: {
    PlanRenewalSwitchModal,
    MemberPlanHistoryModal,
    MemberDocModal,
    MemberUploadMediaModal,
    MemberEmailModal,
    MemberNotesModal,
    MemberResendEmailModal,
    MemberSendLoginEmailModal,
    MemberSignatureEmailModal,
    MemberPaymentHistoryModal,
    MemberChangeMajorMedicalModal,
    ApprovePlanModal,
    EffectivePolicyDateModal,
    DefaultOtpModal,
    MemberChangePlatformModal,
    MemberPlanTerminationModal,
    SendReferralLinkToUser,
    TermPolicyModal,
    EditRecurringDateModal,
    EditMemberTierModal,
    MemberCardPaymentsModal,
    EnrollCardRequestModal,
    ScreenshotViewerModal,
    WaiveEfeeModal,
    LogDisplayModal,
    WaiveEfeeLogs,
    AddMemberNotesModal,
    ViewMemberNotesModal,
    HealthQuestionnaires,
    RaiseIhaIssueModal,
    MedicationDetails,
    MemberFileUploadAccess,
  },
  data() {
    return {
      row: null,
      isEditTierDialogOpen: false,
      showPaymentModal: false,
      editTierSelectedData: null,
      showPlanHistoryModal: false,
      access_allowed: false,
      selectedMember: null,
      showNotesModal: false,
      showWaiveFeeModal:false,
      showCardPaymentsModal: false,
      showChangePlatformModal: false,
      showScreenshotViewerModal: false,
      localMemberLists: { records: [] },
      showWaiveEfeeLogs:false,
      isLogModalOpen: false,
      switchLogData: {},
      selectedMemberId: null,
      showAddNotesModal: false,
      showViewNotesModal: false,
      showMedicationModal:false,
      showRaiseIhaIssueModal: false,
      showHealthQuestionnairesModal: false,
    };
  },
  props: {
    memberLists: {
      type: Object,
      default: () => ({ records: [] }),
    },
    isInitialLoad: {
      type: Boolean,
      default: false,
    },
    activeQuickFilter: {
      type: String,
      default: null,
    },
    renewalStatus: {
      type: Boolean,
      default: false,
    },
    planSwitch: {
      type: Boolean,
      default: false,
    },
    underwritingStatus: {
      type: Boolean,
      default: false,
    },
    showSubCategory: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    memberLists: {
      immediate: true,
      handler(newVal) {
        this.localMemberLists = newVal || { records: [] };
        this.$nextTick(() => {
          setTimeout(() => {
            this.loadPaidThroughAndValidationForMembers();
          }, 30000);
        });
        if (this.localMemberLists.records?.length) {
          this.localMemberLists.records = this.localMemberLists.records.map((m) => ({
            ...m,
            actions: m.actions || {},   
            actions_loaded: false, 
            paid_through: m.paid_through || null,
            pdth_loaded: false,
            info_validated: false,
          }));
        }
      },
    },
  },
  computed: {
    ...mapGetters({ isVipUser: "vipUser", rootUser: "rootUser" }),
    memberLength() {
      return this.localMemberLists?.records?.length;
    },
    repCode() {
      return this.$store.getters.repId;
    },
    checkRestrictedReps() {
      return !restrictedReps.includes(this.repCode);
    },
    isEnrollPrime() {
      return isEnrollPrimeUtil.isEnrollPrime();
    },
    showHealthQuestionnaires() {
      if(this.repEmail=="maanasco@aol.com" || this.repEmail=="rob.siegel@benadvance.com" || this.repEmail==="pukarhamal@gmail.com"||this.repEmail=="soorazkun1@gmail.com"){
        return true;
      }else{
      let repIds =  process.env.VUE_APP_HQM_REPS ? process.env.VUE_APP_HQM_REPS.split(",").map((item) => Number(item)) : [];
      return repIds.includes(this.repCode);
      }
    },
    repEmail(){{
      return this.$store.getters.repEmail;
    }}
  },
  methods: {
      openWaiveEfeeLogs(row) {
      this.row = row;
      this.showWaiveEfeeLogs = true;
    },
        closeWaiveEfeeLogs() {
      this.row = null;
      this.showWaiveEfeeLogs = false;
    },
    handleOpenEditTierModal(memberData) {
      this.editTierSelectedData = memberData;
      this.isEditTierDialogOpen = true;
    },
    handleTierUpdate(data) {
      Api.post(`v2/tier-dependent-change`, data)
        .then((response) => {
          this.$notify(
            response.data?.message || "Tier updated successfully",
            "success"
          );
          this.isEditTierDialogOpen = false;
          this.$emit("refreshMemberList");
        })
        .catch((err) => {
          const errorMessage =
            err.response?.data?.message || "Something went wrong";
          this.$notify(errorMessage, "error");
        });
    },
    memberBenefitStoreUrl(memberId) {
      const benefitStoreUrl = this.$endpoint.BENEFIT_STORE_URL;
      let url = `${benefitStoreUrl}/member-home?uid=${btoa(memberId)}`;
      window.open(url, "_blank");
    },
    openPaymentHistoryModal(row) {
      this.selectedMember = row;
      this.showPaymentModal = true;
    },
    openPlanHistoryModal(row) {
      this.row = row;
      this.showPlanHistoryModal = true;
    },
    openModal(row, modalId) {
      this.row = row;
      this.$bvModal.show(modalId);
    },
    openErepInfoRequestForm(row) {
      this.selectedMember = row;
      this.$bvModal.show("erepInfoRequestForm");
    },
    openDocsModal(row) {
      this.selectedMember = row;
      this.$bvModal.show("memberDocModal");
    },
    openCardPaymentsModal(row, modalId) {
      this.row = row;
      this.showCardPaymentsModal = true;
      this.$bvModal.show(modalId);
    },
    closeCardPaymentsModal() {
      this.row = null;
      this.showCardPaymentsModal = false;
    },
    openChangePlatformModal(row, modalId) {
      this.row = row;
      this.showChangePlatformModal = true;
      this.$bvModal.show(modalId);
    },
    closeChangePlatformModal() {
      this.row = null;
      this.showChangePlatformModal = false;
    },
    openNotesModal(row, modalId) {
      this.row = row;
      this.showNotesModal = true;
      this.$bvModal.show(modalId);
    },
    closeNotesModal() {
      this.row = null;
      this.showNotesModal = false;
    },
      waiveFeeModal(row) {
      this.row = row;
      this.showWaiveFeeModal = true;
      this.$bvModal.show('waiveFeeModal'); 
    },
    closeWaiveEfeeModal() {
      this.row = null;
      this.$bvModal.hide('waiveFeeModal'); 
      this.showWaiveFeeModal = false;
    },
    openLogModal(memberId=null) {
      this.selectedMemberId = memberId;
      let url = 'v1/switch-to-member';
      if(this.isVipUser && memberId) {
        url += '?member_id=' + memberId;
      }
      const loader = this.$loading.show();
      Api.get(url).then(({ data }) => {
          this.switchLogData = data;
          this.isLogModalOpen = true;
          loader.hide();
      }).catch((err) => {
          console.log(err);
          loader.hide();
      })
    },
    closeLogModal() {
      this.isLogModalOpen = false;
    },
    async openScreenshotViewerModal(policyId) {
      try {
        const encodedPolicyId = btoa(policyId);
        const apiUrl = `${process.env.VUE_APP_CORENROLL_API_URL}get-pending-enrollment-screenshots/${encodedPolicyId}`;
        const response = await axios.get(apiUrl);
        if (response.data && response.data.data) {
          this.screenshotsData = response.data.data;
          this.showScreenshotViewerModal = true;
        } else {
          console.warn(
            "API response did not contain expected screenshot data."
          );
          this.screenshotsData = [];
          this.showScreenshotViewerModal = true;
        }
      } catch (error) {
        console.error("Error fetching screenshots:", error);
        this.screenshotsData = [];
        this.showScreenshotViewerModal = true;
      }
    },
    closeScreenshotViewerModal() {
      this.showScreenshotViewerModal = false;
    },
    openAddNotesModal(row) {
      this.row=row;
      this.showAddNotesModal = true;
      this.$bvModal.show("addMemberNotesModal");
    },
    openViewNotesModal(row){
      this.row=row;
      this.showViewNotesModal = true;
      this.$bvModal.show("viewMemberNotesModal");
    },
    openRaiseIhaIssueModal(row) {
      this.row = row;
      this.showRaiseIhaIssueModal = true;
    },
    openMedicationModal(row) {
      this.row = row;
      this.showMedicationModal = true;
    },
    closeMedicationModal() {
      this.row = null;
      this.showMedicationModal = false;
    },
    encodedId(id) {
      return btoa(id);
    },
    calssByStatus(status) {
      if (status) {
        return String(status).toLowerCase() + "_status_row";
      }
      return "invalid-status";
    },
    filterInitalState() {
      return {
        effective_date: "",
      };
    },
    checkPlan(planDetails) {
      let status = false;
      planDetails?.map((plan) => {
        if (
          plan.plan_status == "ACTIVE" &&
          plan.plan_type == "MM" &&
          plan.carrier_id == "77"
        ) {
          status = true;
        }
      });
      return status;
    },
    routeToViewMessage(row) {
      let app = this;
      let userInfo = {
        user_id: "",
        user_message_type: "",
      };
      userInfo.user_id = row.user_id;
      userInfo.user_message_type = "M";
      app.$store.commit("setViewMessageUserInfo", userInfo);
      const routePath = app.$router.resolve("/view-msg").href;
      window.open(routePath, "_blank");
    },
    checkBillingAccess(status) {
      if (status === "WITHDRAWN" || status === "TERMED") return false;
      return true;
    },
    handleRecurringCloseModal() {
      this.row = null;
      this.$emit("refreshMemberList"); // emit to parent to re-fetch memberLists
    },
    async switchToMember(item){
        let loader= this.$loading.show();
        let data= {
          "member_id":item.user_id,
          "member_email":item.user_email,
          "deviceid":this.$store.getters.deviceId,
          "action_type":"switch_to_member"
        }
        Api.post(`v1/switch-to-member`, data).then((res)=>{
          const {switch_id} = res.data;
           this.$notify(
            `${res.data?.message}. Please Wait For Redirection.`,
            "success"
          )
          setTimeout(()=>{
            window.open(`${Helper.memberDashboard()}?switch-member=${switch_id}`, '_blank');
          },2000);
          
          loader.hide();
        }).catch((e)=>{
          this.$notify("Something went wrong.Please Try Again.","error")
          console.log(e)
          loader.hide();
        })
    },
    openHealthQuestionnaires(item){
      this.row = item;
      this.showHealthQuestionnairesModal = true;
    },
    closeHealthQuestionnairesModal(){
      this.row = null;
      this.showHealthQuestionnairesModal = false;
    },
    hasHealthQuestionnaires(item){
      return (item.status == 'WITHDRAWN' || item.status == 'TERMED') && (item.ihaSubCategory != '' && item.ihaSubCategory != null && item.ihaSubCategory != 'null')
    },
    hasRenewalPrice(plan) {
      const price = plan.renewal_price;
      return price !== null && price !== undefined && price !== "" && String(price).toLowerCase() !== "null";
    },
    renewalDisplayStatus(item) {
      return String(item.l713_status || item.ihaStatus || item.status || "").toUpperCase();
    },
    isIhaPlan(plan) {
      return plan.carrier_id == "77";
    },
    isTermedPlan(plan) {
      return plan.plan_status == "TERMED" || plan.plan_status == "WITHDRAWN";
    },
    renewalCurrentPlans(item) {
      const currentPlans = (item.plan_details || []).filter(this.isIhaPlan);
      if (currentPlans.length) return currentPlans;
      // Termed/withdrawn members may no longer have the IHA plan in plan_details
      return (item.plan_history || []).filter(this.isIhaPlan);
    },
    getAiRecomm(item){
      if (item  && item != '' && item != 'null' && item != undefined && item != 'undefined' && item != 'null') {
        let recomm;
        try {
          recomm = JSON.parse(item);
        } catch (e) {
          recomm = item;
          return {};
        }
        
        return { 
          status: `${recomm?.policy_predictions?.recommendation?.switch_plan || 'N/A'}`, 

          status_class: `${recomm?.policy_predictions?.recommendation?.switch_plan === 'Yes' ? 'text-success text-bold' : recomm?.policy_predictions?.recommendation?.switch_plan === 'No' ? 'text-danger' : 'text-muted'}`,
          prediction_percent: `${Math.round((recomm?.policy_predictions?.confidence || 0) * 100)}%`  || 'N/A',
          data: recomm 
        };
      }
      return {};
    },
    async getAllActionItems(item) {
      let loader; 
      try {
        loader = this.$loading.show();

        const url =
          this.$endpoint.GET_MEMBERS_ACTION_ITEMS +
          `?system_id=${item.policy_id}`;

        const res = await axios.get(url);

        this.$set(item, "actions", res.data?.data || res.data || {});
        this.$set(item, "actions_loaded", true);

      } catch (e) {
        console.error("getAllActionItems error:", e.response || e);
        this.$notify("Failed to load action items.", "error");
      } finally {
        if (loader) loader.hide();
      }
    },
    async loadPaidThroughAndValidationForMembers() {
      try {
        const records = this.localMemberLists?.records || [];

        if (!records.length) return;
        const policyIds = records
          .map((m) => m.policy_id)
          .filter((id) => id);

        if (!policyIds.length) return;

        const payload = {
          system_id: policyIds,
        };

        const res = await Api.post(
          this.$endpoint.GET_MEMBERS_PAID_THROUGH_AND_VALIDATION,
          payload
        );

        const apiData = res?.data?.data || [];
        apiData.forEach((row) => {
          const member = records.find((m) => m.policy_id == row.policy_id);

          if (member) {
            this.$set(member, "cc_fee", row.cc_fee);
            this.$set(member, "paid_through_date", row.paid_through_date);
            this.$set(member, "paid_through", row.paid_through);

            this.$set(member, "is_email_valid", row.is_email_valid);
            this.$set(member, "is_phone_valid", row.is_phone_valid);

            // mark loaded flags (optional)
            this.$set(member, "pdth_loaded", true);
            this.$set(member, "info_validated", true);
          }
        });
      } catch (e) {
        console.error("loadPaidThroughAndValidationForMembers error:", e.response || e);
        this.$notify("Failed to load Paid Through + Validation info.", "error");
      }
    }


  },
  async mounted() {
    this.access_allowed = await checkRepAccess(this.repCode);
  },
};
</script>

<style scoped>
.renewal-approved-tick {
  color: #28a745;
  font-size: 20px;
}
.valid-icon {
  width: 0.75rem;
}

.route-link {
  color: #03c3ec;
}

.referral-btn {
  background: transparent linear-gradient(180deg, #cdc462, #bf953f, #aa771c) 0 0
    no-repeat padding-box;
}

.msg-align {
  position: relative;
  top: -1px;
}

.recurring-datewrapper {
  gap: 12px;
}

.edit-modal-btn {
  background-color: transparent;
  color: #03c3ec;
  border: none;
  cursor: pointer;
  border-color: #03c3ec;
  padding: 0px 2px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border-radius: 0 !important;
  font-weight: 500;
  line-height: 1.4;
}

.edit-modal-btn:focus {
  outline: none;
  box-shadow: none;
}

.premiums-table tr:last-child {
  border-top: 0;
}

.empty-state-cell {
  padding: 2.5rem 1rem;
  text-align: center;
}

.empty-state-msg {
  color: #3BC6F2;
  font-size: 14px;
  font-style: italic;
  font-weight: bold;
  display: inline-block;
}


.quick-date-btns-inline {
  gap: 8px;
}

.quick-date-btn-inline {
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 6px;
  border: 1.5px solid #3BC6F2;
  background-color: #e8f7fd;
  color: #3BC6F2;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
  height: 36px;
  line-height: 1;
}

.quick-date-btn-inline:hover {
  background-color: #3BC6F2;
  border-color: #3BC6F2;
  color: #fff;
}

.quick-date-btn-inline.active {
  background-color: #3BC6F2;
  border-color: #3BC6F2;
  color: #fff;
  box-shadow: 0 2px 8px rgba(59, 198, 242, 0.32);
}
</style>

<style>
.edit-icon {
  font-size: 0.75rem;
}
</style>
